import { useId } from "react";

import Stack from "@mui/material/Stack";
import { FormType } from "@simplyk/common";
import { FieldValues, Path, useController, useWatch, UseFormReturn } from "react-hook-form";

import { usePreviewContext } from "../../features/LiveFormEditor/LivePreview/context/PreviewContext";
import { getErrorMessages } from "../../helpers/reactHookForm";
import { isCompanyNameValid, isRequired } from "../../helpers/validators";
import { useTranslate } from "../../hooks/useTranslate";
import { FormCheckbox } from "../forms-design-system/FormCheckbox";
import { FormTextField } from "../forms-design-system/FormTextField/FormTextField";

import { AmplitudeEvents } from "@/constants/amplitude";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";

export type GiveAsOrganismProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  amplitudePayload: Record<string, unknown>;
  formType: FormType;
  helperText?: string;
};

export const FormGiveAsOrganism = <T extends FieldValues>({
  form,
  amplitudePayload,
  formType,
}: React.PropsWithChildren<GiveAsOrganismProps<T>>) => {
  const { t } = useTranslate();
  const { logAmplitudeEventCallback } = useAmplitude();
  const checked = useWatch({ control: form.control, name: "giveAsOrganism" as Path<T> });
  const hasGiftAidConfirmation = useWatch({ control: form.control, name: "hasGiftAidConfirmation" as Path<T> });
  const errors = form.formState.errors;

  const {
    field: { onChange, ...rest },
  } = useController({
    control: form.control,
    name: "giveAsOrganism" as Path<T>,
  });

  const handleChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    onChange(checked);
    logAmplitudeEventCallback(AmplitudeEvents.DonorFormInputChanged, {
      label: "giveAsOrganism",
      errors: getErrorMessages({
        keys: Object.keys(errors),
        errors,
      }),
      ...amplitudePayload,
    });
  };

  return (
    <Stack gap={1}>
      <FormCheckbox
        {...rest}
        id={useId()}
        onChange={handleChange}
        label={formType === FormType.Ticketing ? t("ticketing", "giveAsOrganism") : t("donationForm", "giveAsOrganism")}
      />

      {checked && (
        <FormGiveAsOrganismCompanyName
          form={form}
          amplitudePayload={amplitudePayload}
          formType={formType}
          helperText={hasGiftAidConfirmation && t("common", "giveAsOrganismSwiftAidHelperText")}
        />
      )}
    </Stack>
  );
};

const FormGiveAsOrganismCompanyName = <T extends FieldValues>({
  form,
  amplitudePayload,
  helperText,
}: React.PropsWithChildren<GiveAsOrganismProps<T>>) => {
  const { isPreview } = usePreviewContext();
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();

  const errors = form.formState.errors;

  const handleBlur = () => {
    logAmplitudeEvent(AmplitudeEvents.DonorFormInputChanged, {
      label: "companyName",
      errors: getErrorMessages({ keys: Object.keys(errors), errors }),
      ...amplitudePayload,
    });
  };

  return (
    <FormTextField
      control={form.control}
      name={"companyName" as Path<T>}
      rules={[
        isRequired(t("ticketing", "companyNameRequired")),
        isCompanyNameValid(t("common", "invalidCharacterUsed")),
      ]}
      type="text"
      label={t("ticketing", "companyName")}
      data-test="ticket-form-companyname"
      disableInjectingGlobalStyles={isPreview}
      onBlur={handleBlur}
      error={!!errors.companyName}
      helperText={(errors.companyName?.message as string) || helperText}
      required
      autoFocus
    />
  );
};
